/**
 * Combining several sales pursuits into ONE document — the decisions, with no
 * React and no fetch in any of them.
 *
 * The owner's request: pressing a card's create control should offer the OTHER
 * cards this company is being chased on, so one quote or invoice can cover
 * several projects at once instead of one document per lane.
 *
 * ⚠️ **A PURSUIT IS `(project, company)` AND A DOCUMENT IS ONE COMPANY WITH MANY
 * LINES.** That is the whole shape of the feature: the company is a column on
 * `d_quotes` / `d_invoices`, so it cannot vary across a document — but
 * `f_quote_products.project_id` is per LINE, and a third of live quote lines
 * already carry one. So several pursuits fold into one document exactly when
 * they share a company, and never otherwise.
 *
 * 🔴 **SAME COMPANY ONLY, AND IT IS NOT A FILTER THAT COULD BE RELAXED.** Cards
 * for another company are not offered at all rather than offered-and-refused —
 * there is no payload that could express two companies, so an offer would be a
 * control whose only outcome is an error. Owner's decision.
 *
 * ⚠️ **NOTHING HERE MOVES A CARD, AND NOTHING MAY BE ADDED THAT DOES.** A
 * pursuit's status is advanced SERVER-side when the resulting document reaches
 * its trigger — `sales-pursuit-sync.service.ts` reconciles each `(company,
 * project)` pair when a quote is sent or an invoice is billed. One rule,
 * whether the document came from this board or from `/quotes`. An optimistic
 * move here would be a second writer of that state, and it would be wrong for
 * every document that is saved and then never sent.
 *
 * ⚠️ **SEEDING IS NOT VALIDATING.** The seven line-project refusals in
 * `@/lib/line-project` are server-side and stay there: a project that does not
 * sell a line's product is refusal 5, a retired project is refusal 4, and both
 * are `422`s naming `projectId` that route to that row's own control. The
 * product picker here NARROWS what is offered; it does not reproduce the guard,
 * because the guard runs on the POST-MERGE state that only the server can see.
 */

import type { CompanyCreateTarget } from '@/components/records/RecordCreateMenu';
import type { PlannedProductLine } from '@/lib/api';
import type { MasterRef, Project, SalesPursuit, SalesPursuitStatus } from '@/lib/types';

/**
 * Which document a card's create menu is raising.
 *
 * 🔴 **`'deal'` IS A MEMBER SINCE 2026-09-14, ON THE OWNER'S INSTRUCTION, AND THE
 * UNION USED TO BE THE GUARD AGAINST IT.** *SUPERSEDED, and it was stated in six
 * places: "⛔ **THERE IS NO `'deal'` MEMBER AND THERE MUST NOT BE ONE.**
 * `DealFormModal` takes `lockedCompany` and nothing else — no `prefill`, no
 * `initialDraftRows` on its line grid — so a deal raised through this would
 * collect a basket and drop every line and every project, create cleanly, and
 * advance no card."* **That seam was built on 2026-09-13** for the company "+"
 * seeding flow: `DealFormPrefill.lines` and `DealLineItemsDraftProps
 * .initialDraftRows` both exist, both take `PlannedProductLine`, and
 * `seededRowOf` in that grid is the one place a seed becomes a row.
 *
 * ⚠️ **THE REASONING THAT SURVIVES IS WHY IT HAD TO BE A SEAM RATHER THAN A
 * URL.** A deal, once created, still cannot be un-created, and the server's
 * `(company, project)` sync still advances nothing for a document whose lines
 * carry no project. Those are the reasons the basket has to reach the grid, not
 * reasons to withhold the item — and they are why nothing on this path may
 * create anything before somebody presses Save.
 */
export type CombineKind = 'deal' | 'quote' | 'invoice';

/**
 * A create-menu module, as the kind of document it raises.
 *
 * ⚠️ **ONE COPY, BECAUSE THERE ARE TWO CALL SITES** — the board card and the
 * table's actions cell — and they are in different files. Two copies of a
 * three-way map agree the day they are written; what would drift here is which
 * of the three records a person gets when they press an item.
 *
 * ⚠️ **`CompaniesPage` CARRIES A TWIN OF THIS MAP INLINE** (`openProjectLines`,
 * over `CompanyProjectLinesKind`) and it is deliberately NOT imported from
 * here — the same ruling `CompanyProjectLinesFlow` records for its own twin of
 * `productsOf`: that is another feature's flow, not a library, and importing a
 * private helper across two features is how one of them ends up unable to change
 * it. ⚠️ **On a THIRD feature needing it, both move DOWN into `@/lib`**, which is
 * the exit condition that file names too. Two is a twin; three is a library.
 *
 * ⚠️ **`satisfies Record<…>` IS THE EXHAUSTIVENESS CHECK AND IS NOT DECORATION.**
 * A fourth entry in `COMPANY_CREATE_TARGETS` fails the build here rather than
 * arriving as a menu item that silently falls through to a URL — which is
 * exactly the state Deal was in, and it read as deliberate for months.
 */
const KIND_BY_MODULE = {
  deals: 'deal',
  quotes: 'quote',
  invoices: 'invoice',
} as const satisfies Record<CompanyCreateTarget['module'], CombineKind>;

/**
 * ⚠️ **TOTAL, so every item of the create menu is now intercepted.** The two call
 * sites therefore return `true` unconditionally from `onSelect`: nothing on this
 * board navigates to `?companyId=N&new=1` any more. That URL stays on the item
 * as its `href` — the honest destination if the interception is ever removed —
 * and `RecordCreateMenuItem.onSelect` documents why it is kept.
 */
export function combineKindOf(module: CompanyCreateTarget['module']): CombineKind {
  return KIND_BY_MODULE[module];
}

/**
 * The lane that means *a quote is out and nobody has come back on it yet*.
 *
 * ⚠️ **RESOLVING THIS LANE BY SLUG IS THE HOUSE ANSWER, COPIED RATHER THAN
 * INVENTED.** The backend does the identical thing in the identical place —
 * `QUOTED_LANE_SLUG` in `sales-pursuit-sync.service.ts`, which resolves the
 * lane a SENT or ACCEPTED quote asks for — and its reasoning transfers whole:
 * migration 162 makes `f_sales_pursuit_statuses.slug` UNIQUE and
 * **write-on-create-only** (absent from any update schema, and this master has
 * no admin CRUD route at all — `GET /sales-tracker/statuses` is the only
 * endpoint), and says in as many words that the slug is *"the only thing any
 * consumer may resolve a lane by"*. `name` is the administrator's to edit and
 * nothing here reads it.
 *
 * ⚠️ **THIS IS NOT THE THING `SalesPursuitStatus.slug`'s OWN DOC FORBIDS.** That
 * warning — *"so a client can address a status stably, not so a client can
 * branch on it"* — is about reading a slug AS A FLAG, the tidier cousin of
 * matching a name. Booked and Lost have flags and are resolved by them below.
 * This lane has none, so addressing it by its stable key is the only handle
 * that exists.
 *
 * ⛔ **EXACT MATCH, NEVER CONTAINMENT.** `quoted_out` contains `quoted`.
 *
 * ✅ **IF AN `isQuoted` FLAG EVER LANDS, THIS CONSTANT GOES** and the resolution
 * joins the other two — the same sentence the backend's copy carries, and the
 * same shape migration 106 already used when `is_sent` was added to
 * `f_quote_stages` for exactly this class of question.
 */
export const QUOTED_LANE_SLUG = 'quoted';

/**
 * One pursuit that may be folded into the document being raised.
 *
 * `products` is what the pursuit's PROJECT sells, which is the option list for
 * that row's product picker.
 */
export interface CombineCandidate {
  pursuit: SalesPursuit;
  /** The lane it sits in, or `null` when the board does not describe that lane. */
  status: SalesPursuitStatus | null;
  projectId: number;
  /**
   * The project's name, or `null` when it could not be read.
   *
   * ⚠️ **`null` DISABLES THE ROW RATHER THAN SEEDING A HANDLE.** The name is not
   * decoration: `PlannedProductLine.project` carries id AND name because the
   * line grid's project `Combobox` falls back to its placeholder when the value
   * is absent from its options, so a seed with no name renders an EMPTY control
   * over a real `projectId` that `createPayload` still sends — the screen saying
   * no project and the wire saying one. A fabricated `Project #12` would pin
   * that control to a label that is not the project's name, on a row somebody
   * then saves.
   */
  projectName: string | null;
  /**
   * Everything that project sells (`f_project_products`), for the row's picker.
   *
   * ⚠️ **`[]` MEANS THE PROJECT COULD NOT BE READ, NOT THAT IT SELLS NOTHING.**
   * `d_projects.product_id` stayed NOT NULL as the primary and the server
   * re-asserts it into the set on every write, so a project ALWAYS sells at
   * least one product. An empty array here is a failed or short read, and the
   * row is disabled rather than offered with an empty dropdown.
   */
  products: MasterRef[];
  /**
   * This pursuit has been past the quoting point already.
   *
   * ⚠️ **IT IS A FACT THE READER CANNOT OTHERWISE SEE, AND IT IS WHY THE ROW IS
   * FLAGGED.** Owner's decision: a Negotiating card may be combined, and
   * combining it produces a REVISED quote rather than a first one. Without the
   * flag the list reads as four equivalent un-quoted opportunities.
   */
  alreadyQuoted: boolean;
}

/**
 * The Quoted lane, or `null` when the board cannot express one.
 *
 * ⚠️ **ACTIVE ONLY, mirroring `resolveTargetLane` on the server.** A retired
 * lane is a POSITION but not a DESTINATION, and if Quoted were retired the sync
 * could not move anything into it either — so the concept is broken rather than
 * merely unusual, and answering `null` is the honest report of that.
 */
export function quotedLaneOf(
  statuses: readonly SalesPursuitStatus[],
): SalesPursuitStatus | null {
  return statuses.find((status) => status.isActive && status.slug === QUOTED_LANE_SLUG) ?? null;
}

/**
 * Whether a pursuit sitting in this lane may be added to a document.
 *
 * The owner's rule is *anything before Quoted, plus Negotiating* — which is,
 * stated as data, **everything that is not Quoted, not Booked and not Lost.**
 *
 * ⚠️ **IT FAILS CLOSED IN EVERY UNKNOWN, AND THAT IS THE SAFE DIRECTION HERE.**
 * An unresolvable lane, or a board with no Quoted lane at all, yields `false`:
 * the card is simply not offered, which costs one missing option that a person
 * can still raise the ordinary way. The other direction offers a card somebody
 * has already quoted or booked and lets them sell the same work twice, on a
 * document a customer receives.
 *
 * ⚠️ **RETIREMENT IS DELIBERATELY NOT CONSULTED.** A retired lane is a position,
 * and nothing here moves a card into one — the question is what commercial
 * stage the pursuit is at, not whether it is a legal drop target. A pursuit
 * parked in a retired "Contacted (old)" lane is still un-quoted work.
 */
export function isCombinable(
  status: SalesPursuitStatus | null,
  quotedLane: SalesPursuitStatus | null,
): boolean {
  if (status === null || quotedLane === null) return false;
  if (status.isBooked || status.isLost) return false;
  return status.id !== quotedLane.id;
}

/**
 * Whether this lane sits PAST the quoting point.
 *
 * ⚠️ **RANKED ON `sortOrder`, WHICH IS THE ADMINISTRATOR'S OWN ORDER** — the
 * same thing `buildLaneOrder` on the server treats as authoritative, and the
 * same order the board draws its columns in. It generalises: a lane somebody
 * adds after Quoted is correctly flagged without this file changing, where a
 * second slug constant would have to be extended by hand and would silently
 * answer `false` until somebody remembered.
 */
export function wasAlreadyQuoted(
  status: SalesPursuitStatus | null,
  quotedLane: SalesPursuitStatus | null,
): boolean {
  if (status === null || quotedLane === null) return false;
  return status.sortOrder > quotedLane.sortOrder;
}

/** What {@link combineCandidatesOf} needs. Plain data, so a test calls it directly. */
export interface CombineCandidateInput {
  /** The card the flow was started from. It is never a candidate — it is the anchor. */
  source: SalesPursuit;
  /** Every pursuit this company is being chased on, in this cycle. */
  pursuits: readonly SalesPursuit[];
  /** The board's status list — ACTIVE AND RETIRED, exactly as served. */
  statuses: readonly SalesPursuitStatus[];
  /** Projects read for this cycle, for names and for the products each sells. */
  projects: readonly Project[];
}

/**
 * The other pursuits this company has that may be folded in, ordered for reading.
 *
 * ⚠️ **THE SOURCE IS EXCLUDED HERE AND ADDED BACK BY THE DIALOG.** Its line is
 * always present and is not removable — see `CombinePursuitsDialog` — so it is
 * not a *candidate*, and including it would make "how many others are there"
 * (which decides whether the dialog opens at all) off by one.
 *
 * ⚠️ **A DIFFERENT COMPANY CANNOT APPEAR, and the guard is here rather than at
 * the call site.** The caller fetches with `?companyId=`, so the filter is
 * normally redundant — which is exactly why it is kept: a caller that forgot the
 * parameter would otherwise silently offer the whole cycle, and every row of it
 * would be refused by a server that only ever sees one `companyId`.
 *
 * **Ordered by project name, A–Z.** The list endpoint's own `sortOrder` is a
 * position WITHIN a lane and means nothing across lanes, so it would arrive in
 * an order with no reading. Rows with no resolvable name sort last, where a
 * disabled row belongs.
 */
export function combineCandidatesOf(input: CombineCandidateInput): CombineCandidate[] {
  const { source, pursuits, statuses, projects } = input;
  const quotedLane = quotedLaneOf(statuses);
  const statusById = new Map(statuses.map((status) => [status.id, status]));
  const projectById = new Map(projects.map((project) => [project.id, project]));

  const candidates: CombineCandidate[] = [];
  for (const pursuit of pursuits) {
    if (pursuit.id === source.id) continue;
    if (pursuit.company.id !== source.company.id) continue;
    if (pursuit.projectId === source.projectId) continue;

    const status = statusById.get(pursuit.statusId) ?? null;
    if (!isCombinable(status, quotedLane)) continue;

    const project = projectById.get(pursuit.projectId);
    candidates.push({
      pursuit,
      status,
      projectId: pursuit.projectId,
      projectName: project?.name ?? null,
      /*
       * `products` is the SET. Reading the primary alone would silently drop a
       * project's secondary products — the exact narrowing migration 156 exists
       * to end — and it is the same set `projectSellsProduct` reads in
       * `@/lib/use-line-project`.
       */
      products: project === undefined ? [] : productsOf(project),
      alreadyQuoted: wasAlreadyQuoted(status, quotedLane),
    });
  }

  return candidates.sort(byProjectName);
}

/**
 * Everything a project sells.
 *
 * ⚠️ **`products` IS THE SET, and it is now the only thing read here.** Since
 * migration 156 a project sells several, and `products[0]` is the primary by the
 * read's `ORDER BY` — an API guarantee, not an incidental ordering. Matching the
 * primary alone excludes a project that sells the wanted product as a SECONDARY,
 * and the exclusion is silent.
 *
 * ✅ **The deprecated-singular fallback is GONE — 2026-09-18.** ⚠️ *SUPERSEDED:
 * "The `product` fallback is for a server older than that expand/contract and
 * nothing else."* It was a reader of the key the backend is withdrawing, and it
 * was already unreachable — `ensurePrimaryProduct` re-asserts the primary into
 * the set on every write, so `products` is never empty against a server that has
 * run 156. ⛔ **And its guard tested `=== null` where a withdrawn key reads
 * `undefined`**, so the dead branch would have returned `[undefined]`.
 */
function productsOf(project: Project): MasterRef[] {
  return project.products;
}

function byProjectName(a: CombineCandidate, b: CombineCandidate): number {
  if (a.projectName === null && b.projectName === null) return a.projectId - b.projectId;
  if (a.projectName === null) return 1;
  if (b.projectName === null) return -1;
  return a.projectName.localeCompare(b.projectName);
}

/**
 * A pursuit's `units`, expressed as a line item's quantity.
 *
 * A line quantity is a real JSON number and an **integer of at least 1**, where
 * `d_sales_pursuits.units` is `INT UNSIGNED NULL`. The three cases are three
 * different facts and only one of them is a refusal:
 *
 * | `units` | seeds | why |
 * | --- | --- | --- |
 * | `n >= 1` | `n` | the figure somebody recorded |
 * | `0` | **nothing, and the save is BLOCKED** | `0` is a real zero somebody typed, and a line cannot be zero. Substituting 1 writes a figure nobody chose onto a document a customer is billed against |
 * | `null` | `1` | nobody has said how many, so there is nothing to preserve |
 *
 * ⚠️ **THE `null` ROW IS NOT THE SUBSTITUTION `PlannedProductLine.lineQuantity`
 * FORBIDS, AND THE DIFFERENCE IS THE WHOLE POINT.** That prohibition is about a
 * STATED planned figure a line cannot express — `"2.50"` rounded to 1 is a
 * claim replaced by a different claim. `units: null` states nothing at all, and
 * `1` is what every ordinary new line in this app already opens at
 * (`blankDraft()`), so the row arrives exactly as it would if somebody had added
 * it by hand. The owner's decision says so in terms: *seed where it exists,
 * leave blank where it does not, and do NOT render the blank case as a failure*
 * — and an empty quantity box is not "blank", it is a blocked save.
 *
 * ⚠️ **`0` IS THEREFORE THE ONE CASE THAT BLOCKS**, and it should: somebody
 * answered the question with a number a line item cannot carry, and the grid
 * names the planned figure beside the empty box until they settle it.
 */
export function lineQuantityOf(units: number | null): number | null {
  if (units === null) return 1;
  if (!Number.isInteger(units) || units < 1) return null;
  return units;
}

/** What {@link plannedLineOf} needs to build one seeded row. */
export interface SeededLineInput {
  units: number | null;
  projectId: number;
  projectName: string;
  product: MasterRef;
}

/**
 * One pursuit, shaped as a draft line for `QuoteFormModal` / `InvoiceFormModal`.
 *
 * ⚠️ **`PlannedProductLine` IS REUSED RATHER THAN MIRRORED, and that is
 * deliberate.** It is the type both form modals' `prefill.lines` already take,
 * `QuoteLineItemsTable`'s `seededRowOf` is the one place a seed becomes a row,
 * and its rules — an unstatable quantity blocks the save, a `null` description
 * leaves the cell DERIVING, a `null` price omits the key so the server snapshots
 * the catalogue figure — are decisions this feature wants unchanged. A
 * look-alike type would be a second home for every one of them.
 *
 * 🔴 **THE PROJECT IS THE REASON THE FEATURE WORKS AT ALL.** The server's sync
 * advances a card when a document names its `(company, project)` pair, so a
 * document whose lines carry no project moves NOTHING — with the document itself
 * perfectly correct, a `201`, and nothing failing anywhere. The name travels
 * beside the id because the line's project `Combobox` renders empty over an id
 * it cannot find in its options, which is every first paint.
 *
 * ⚠️ **NO PRICE AND NO DESCRIPTION, and both `null`s are load-bearing.** A
 * pursuit records no money — migration 163 has no amount column and its header
 * forbids adding one — so there is nothing to seed and `null` leaves the server
 * to snapshot the catalogue price at the moment the line is written.
 * `description: null` leaves the cell deriving from the product and its cities;
 * writing anything there, the product name included, freezes it permanently.
 *
 * ⚠️ **NO CITIES, EVER.** A city write is a whole-set REPLACE and a seed cannot
 * know which cities were meant; `seededRowOf` spreads over `blankDraft()`, whose
 * `cityIds` is already `[]`, so a chosen project leaves the row in exactly the
 * state the server judges POST-MERGE.
 */
export function plannedLineOf(input: SeededLineInput): PlannedProductLine {
  const { units, projectId, projectName, product } = input;
  const lineQuantity = lineQuantityOf(units);
  return {
    productId: product.id,
    /*
     * A project's products are `MasterRef` — `{id, name}` — so there is no code
     * to carry. `null` is "nobody told us", which is what the Item cell renders
     * for any product whose code is unset; inventing one from the name would be
     * a label that looks like a catalogue reference and is not.
     */
    productCode: null,
    productName: product.name,
    project: { id: projectId, name: projectName },
    /*
     * Read ONLY when `lineQuantity` is null, as the text of the refusal — see
     * `seededRowOf`'s `plannedQuantity`. So it must state the figure a person
     * actually recorded, which for the blocking case is the literal `0`.
     */
    quantity: units === null ? '1' : String(units),
    lineQuantity,
    plannedAmount: null,
    unitPrice: null,
    /*
     * `true` because nothing was claimed. `unitPriceExact: false` means "the
     * seeded price will not add up to the plan", and there is no plan and no
     * price here to disagree with.
     */
    unitPriceExact: true,
    description: null,
  };
}
